import { ReactNode, useState } from "react"
import styled from "styled-components"

import { useLazyGetEnterpriseContractorsQuery } from "store/api/contractorsApi"
import { useAppSelector } from "store/hooks"
import { ApiTypes, UITypes } from "types"
import styles from "assets/scss/index.module.scss"
import { ReactComponent as TopArrow } from "assets/svg/contractor-top-arrow.svg"

import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { AddContractorModal } from "modals/AddContractorModal"
import { EnterpriseAccountModal } from "modals/EnterpriseAccountModal"
import { ButtonNew } from "components/form/ButtonNew"

import { AddContractorButton } from "./AddContractorButton"
import { convertEnterpriseContractorsToTableRows } from "./enterpriseContractorsTable"

const ReverseArrow = styled(TopArrow)`
  transform: rotate(180deg);
`

const StyledTabsWrapper = styled.div`
  width: 100%;
`

const TabsContainer = styled.div`
  width: 100%;
  display: flex;
  margin-top: 16px;
  border-bottom: 1px solid var(--border-strong);
  margin-bottom: 16px;
`

const Tab = styled.button<{ $active: boolean; $tabColor?: string }>`
  color: ${({ $active, $tabColor }) => {
    if ($tabColor) {
      return $tabColor
    }
    return $active ? "#334155" : "#64748b"
  }};
  margin-right: 24px;
  font-size: 14px;
  font-weight: 500;
  padding-bottom: 14px;
  margin-bottom: -1px;
  border: none;
  border-bottom: 1px solid ${({ $active }) => ($active ? "#2db2c8" : "transparent")};
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  opacity: ${({ $active }) => ($active ? 1 : 0.7)};

  &:hover {
    opacity: 1;
    color: ${({ $tabColor }) => $tabColor || "#334155"};
  }
`

interface HeaderProps {
  tableHeaderCells: UITypes.TableHeaderCell[]
  meta?: ApiTypes.Model.Pagination
  onAddContractorSuccess: () => void
  renderTableControls?: () => ReactNode
}

const Header = ({ tableHeaderCells, meta, onAddContractorSuccess, renderTableControls }: HeaderProps) => {
  const [showAddContractorModal, setShowAddContractorModal] = useState(false)
  const [showAddEnterpriseModal, setShowAddEnterpriseModal] = useState(false)
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const statusOptions = useAppSelector(state => state.tempData.contractorAccountStatusOptions) || []

  const tabs = statusOptions.map(option => ({
    label: option.label,
    value: String(option.value),
    color: option.color,
  }))

  const getExportParams = () => {
    if (urlQueryParams.top15 || urlQueryParams.bottom15) {
      return {
        order_column: "installed",
        order_type: urlQueryParams.top15 ? "DESC" : "ASC",
        per_page: 15,
        page: 1,
      }
    }

    return urlQueryParams
  }

  const getCSVExportMeta = () => {
    if (meta && (urlQueryParams.top15 || urlQueryParams.bottom15)) {
      return {
        ...meta,
        total: 15,
      }
    }

    return meta
  }

  const { handleExport } = useExportCSV({
    params: getExportParams(),
    headerCells: tableHeaderCells,
    meta: getCSVExportMeta(),
    fileName: "contractors-v2",
    convertData: convertEnterpriseContractorsToTableRows,
    useLazyQuery: useLazyGetEnterpriseContractorsQuery,
  })

  const setSearchParam = (param: "top15" | "bottom15") => {
    setUrlQueryParams({ [param]: true }, true)
  }

  const handleAddContractorSuccess = () => {
    setShowAddContractorModal(false)
    setShowAddEnterpriseModal(false)
    onAddContractorSuccess()
  }

  const handleStatusTabClick = (status: string) => {
    setUrlQueryParams({ account_status_id: urlQueryParams.account_status_id === status ? null : status }, true)
  }

  return (
    <>
      <nav className={styles.tableNavWrapper}>
        <AddContractorButton
          onAddContractor={() => setShowAddContractorModal(true)}
          onAddEnterprise={() => setShowAddEnterpriseModal(true)}
        />
        <div className={styles.tableNavButtonsWrapper}>
          {renderTableControls?.()}
          <ButtonNew
            color="tropicalCyan"
            onClick={() => setSearchParam("top15")}
            className={styles.tableNavItem}
            disabled={meta?.total === 0}
          >
            <TopArrow />
            Top 15
          </ButtonNew>
          <ButtonNew
            color="tropicalCyan"
            onClick={() => setSearchParam("bottom15")}
            className={styles.tableNavItem}
            disabled={meta?.total === 0}
          >
            <ReverseArrow />
            Bottom 15
          </ButtonNew>
          <ButtonNew
            color="tropicalCyan"
            onClick={handleExport}
            className={styles.tableNavItem}
            disabled={meta?.total === 0}
          >
            Export
          </ButtonNew>
        </div>
      </nav>
      <StyledTabsWrapper>
        <TabsContainer>
          {tabs.map(tab => (
            <Tab
              key={tab.value}
              $active={urlQueryParams.account_status_id === tab.value}
              $tabColor={tab.color}
              onClick={() => handleStatusTabClick(tab.value)}
            >
              {tab.label}
            </Tab>
          ))}
        </TabsContainer>
      </StyledTabsWrapper>
      {showAddContractorModal && (
        <AddContractorModal onCancel={() => setShowAddContractorModal(false)} onSuccess={handleAddContractorSuccess} />
      )}
      {showAddEnterpriseModal && (
        <EnterpriseAccountModal
          onCancel={() => setShowAddEnterpriseModal(false)}
          onSuccess={handleAddContractorSuccess}
        />
      )}
    </>
  )
}

export default Header
